import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import mimeDb from 'mime-db';
import { intlShape } from '../components/base/IntlProviderWrapper';
import notificationDecorator from '../modules/Notifications/notificationDecorator';
import {
  CITIZENSHIP_TYPE, DEFAULT_ITEMS_OF_PAGE, GENDER_TYPE,
} from '../configs';
import { required } from './validation';

export const getResponseError = (response, defaultMessage = '') => {
  const {
    formatMessage,
  } = intlShape;
  if (response && response.error) {
    if (typeof response.error === 'string') {
      return response.error;
    }
    let { message = '' } = response.error;
    if (response.error.code && !message) {
      message = formatMessage({ id: `error.code.${response.error.code}`, defaultMessage: ' ' }).trim();
    }

    return message || defaultMessage || formatMessage({ id: 'error.default' });
  }

  return response?.message || defaultMessage || formatMessage({ id: 'error.default' });
};

export const setFormError = (response, setError = () => {}) => {
  if (!response || !response.errors) {
    return;
  }
  const { errors } = response;
  const keys = Object.keys(errors);
  for (let i = 0, len = keys.length; i < len; i++) {
    const key = keys[i];
    setError(key, { type: 'server', message: Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key] }, { shouldFocus: !i });
  }
};

export const saveFilter = (value, name) => {
  if (name) {
    window.localStorage.setItem(name, JSON.stringify(value));
  }
};
export const getSavedFilter = name => {
  if (name) {
    return getJSONFromString(window.localStorage.getItem(name)) || [];
  }
};

export const getFirstFormError = (errors = {}, prevName = []) => {
  const keys = Object.keys(errors);
  if (!keys || !keys.length) return null;
  const firstElement = errors[keys[0]];
  if (firstElement && firstElement.type && firstElement.message && firstElement.ref) {
    firstElement.name = [...prevName, keys[0]].join('.');
    return firstElement;
  }
  return getFirstFormError(firstElement, [...prevName, keys[0]]);
};

export const isObject = object => object != null && typeof object === 'object';

export const deepEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2))
      || (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

export const isClear = obj => Object.values(obj).reduce((acc, cur) => {
  const value = cur;
  if (isObject(value)) {
    return acc && isClear(value);
  } if (Array.isArray(value)) {
    return acc && !value.length;
  }
  return acc && !value;
}, true);

export const fallbackCopyTextToClipboard = text => {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (!successful) {
      window.console.error('Fallback: Oops, unable to copy');
    }
  } catch (err) {
    window.console.error('Fallback: Oops, unable to copy', err);
  }
  document.body.removeChild(textArea);
};

export const copyTextToClipboard = text => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(() => {}, err => {
    window.console.error('Async: Could not copy text: ', err);
  });
};

export const getPathNameWithOutParameters = (path = '') => {
  if (!path) {
    return path;
  }
  return path.split(':')[0];
};

export const addZero = (len = 1, part = '') => {
  const arrayLen = len - String(part).length >= 0 ? len - String(part).length : 0;
  return (
    Array(arrayLen)
      .fill('0')
      .join('') + String(part)
  );
};

export const responseLog = response => window.console.error(response);

export const responseLogWithNotification = (response, title = '', message = '') => {
  const {
    formatMessage,
  } = intlShape;
  window.console.error(response);
  notificationDecorator({
    title: title || formatMessage({ id: 'load.data' }),
    message: getResponseError(response, message) || formatMessage({ id: 'failed' }),
    isError: true,
  });
};

export const getPaginationOpts = (listMeta = {}, opts = {}) => {
  const page = opts && !Number.isNaN(Number(opts.page))
    ? Number(opts.page)
    : listMeta && listMeta.page && !!Number(listMeta.page)
      ? Number(listMeta.page)
      : 1;
  const limit = opts && opts.limit && !!Number(opts.limit)
    ? Number(opts.limit)
    : listMeta && listMeta.limit && !!Number(listMeta.limit)
      ? Number(listMeta.limit)
      : DEFAULT_ITEMS_OF_PAGE;
  return {
    page,
    limit,
  };
};

export const getJSONFromString = value => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

export class PermissionDeniedError extends Error {
  constructor(message, options) {
    // Need to pass `options` as the second parameter to install the "cause" property.
    super(message, options);
    this.status = 403;
  }
}

export const RegNumEnToRu = text => {
  if (!text) {
    return text;
  }
  text = text
    .replace(/A/g, 'А')
    .replace(/B/g, 'В')
    .replace(/E/g, 'Е')
    .replace(/K/g, 'К')
    .replace(/M/g, 'М')
    .replace(/H/g, 'Н')
    .replace(/O/g, 'О')
    .replace(/P/g, 'Р')
    .replace(/C/g, 'С')
    .replace(/T/g, 'Т')
    .replace(/Y/g, 'У')
    .replace(/X/g, 'Х');
  return text;
};

export function getFilterData(name, defaultValue = null, adds = null) {
  const res = getJsonFromStorage(name, defaultValue);
  if (adds && typeof adds === 'object') {
    const keys = Object.keys(adds);
    for (let i = 0, len = keys.length; i < len; i++) {
      if (typeof adds[keys[i]] !== 'undefined') {
        res[keys[i]] = adds[keys[i]];
      }
    }
  }
  return res;
}

export function getJsonFromStorage(name, defaultValue = null) {
  const value = localStorage.getItem(name);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return defaultValue;
    }
  }
  return defaultValue;
}

export const useStoreState = function useStoreState(initialState, name) {
  const [value, setter] = useState(getJsonFromStorage(name) || initialState);
  const setValue = value => {
    setter(value);
  };
  const save = value => {
    localStorage.setItem(name, JSON.stringify(value));
  };
  const reset = () => setValue(getJsonFromStorage(name) || initialState);
  return [value, setValue, reset, save];
};

window.updateTimers = [];

export const useUpdatePeriod = (
  callback = Promise.resolve,
  updatePeriod = 0,
  noPromise = false,
) => {
  const timerRef = useRef(null);
  const periodRef = useRef(updatePeriod);
  const stoppedRef = useRef(false);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    periodRef.current = updatePeriod ?? 0;
  }, [updatePeriod]);

  const clear = useCallback(() => {
    if (!timerRef.current) {
      return;
    }

    const index = window.updateTimers.findIndex(
      i => i === timerRef.current,
    );

    if (index > -1) {
      window.updateTimers.splice(index, 1);
    }

    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const createTimer = useCallback(() => {
    timerRef.current = setTimeout(load, periodRef.current * 1000);
    window.updateTimers.push(timerRef.current);
  }, []);

  const load = useCallback(() => {
    clear();

    const result = callbackRef.current();

    if (noPromise) {
      if (periodRef.current && !stoppedRef.current) {
        createTimer();
      }
      return;
    }

    Promise.resolve(result).finally(() => {
      if (periodRef.current && !stoppedRef.current) {
        createTimer();
      }
    });
  }, [clear, createTimer, noPromise]);

  const destroy = useCallback(() => {
    stoppedRef.current = true;
    clear();
  }, [clear]);

  const setPeriod = useCallback(
    value => {
      stoppedRef.current = false;
      periodRef.current = value ?? 0;
      load();
      return destroy;
    },
    [load, destroy],
  );

  useEffect(() => destroy, [destroy]);

  return [setPeriod, destroy, clear, load, createTimer];
};

export const nameBySave = (text = '') => {
  const res = text.replace(/[^a-zа-яё0-9 ._№\-\s]/gi, '_');
  return res.replace(/^[^a-zа-яё0-9\s]*/gi, '');
};

export const getMomentValue = (value, format) => {
  let v = moment(value, format);
  if (!v || !v.isValid()) {
    v = moment();
  }
  return v;
};

export const getURLParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const data = {};
  for (const key of searchParams.keys()) {
    data[key] = searchParams.get(key);
  }
  return data;
};

export const sizeFormatter = (size = 0) => {
  const { formatNumber } = intlShape;
  const k = 1024;
  const sizes = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
  if (!size) {
    return formatNumber(0, { style: 'unit', unit: 'byte' });
  }
  const i = Math.floor(Math.log(size) / Math.log(k));
  const shortSize = parseFloat((size / k ** i).toFixed(2));
  return formatNumber(shortSize, { style: 'unit', unit: sizes[i] });
};

export const getExt = fileMime => {
  const data = mimeDb[fileMime];
  if (!data) return '';
  const exts = data.extensions.filter(f => f.length <= 3);
  const exts4 = data.extensions.filter(f => f.length <= 4);
  if (!exts || !exts.length) return exts4.length ? exts4[0] : '';
  return exts[0];
};

export const getKeyByValue = (object, value) => Object.keys(object).find(key => object[key] === value);

export const getIsRequired = (dtoObject, customValidator) => (dtoObject
      && dtoObject.validators
      && dtoObject.validators.length
      && !!dtoObject.validators.find(validator => validator.toString() === required.toString()))
    || !!customValidator.find(validator => validator.toString() === required.toString());

export const createClearObject = fields => Object.keys(fields).reduce((acc, key) => {
  const field = fields[key];
  const isArray = Array.isArray(field);
  if (field && typeof field === 'object' && !isArray) {
    acc[key] = createClearObject(field);
  } else if (isArray) {
    acc[key] = [];
  } else {
    acc[key] = '';
  }
  return acc;
}, {});

export const getFieldValidation = (meta = {}, fields) => {
  const { check_errors = {}, errors = {} } = meta;
  const result = {
    check_errors: [],
    errors: [],
  };
  let names = [fields];
  if (Array.isArray(fields)) {
    names = fields;
  }
  for (const n of names) {
    if (check_errors[n]?.messages) {
      result.check_errors = [...result.check_errors, ...check_errors[n].messages];
    }
    if (errors?.[n]?.messages) {
      result.errors = [...result.errors, ...errors[n].messages];
    }
  }
  return result;
};

export const detectExt = base => new Promise((resolve, reject) => {
  const i = new XMLHttpRequest();
  i.onload = resolve;
  i.onerror = reject;
  i.open('GET', `chrome-extension://${base}/views/app.html`);
  i.send();
});

export const getDeepValue = (obj = {}, path = '') => {
  const pathParts = path.split('.');
  let newValue = { ...obj };
  for (let i = 0; i < pathParts.length; i++) {
    if (newValue[pathParts[i]] === undefined || newValue[pathParts[i]] === null) {
      return undefined;
    }
    newValue = newValue[pathParts[i]];
  }
  return newValue;
};

export const addressTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = String(v)
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const companyNameTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = replaceQuotes(String(v))
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ')
      .toUpperCase();
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const nameTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    const exceptions = ['оглы'];
    e.target.value = String(v).toLowerCase()
      .split(' ')
      .map(w => {
        if (exceptions.includes(w)) { return w; }
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const grzTransliterationTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;

    const enToRuMap = {
      A: 'А',
      B: 'В',
      C: 'С',
      E: 'Е',
      H: 'Н',
      K: 'К',
      M: 'М',
      O: 'О',
      P: 'Р',
      T: 'Т',
      X: 'Х',
      Y: 'У',
    };

    e.target.value = v
      .toUpperCase()
      .split('')
      .map(char => enToRuMap[char] || char)
      .join('');

    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const replaceQuotes = text => {
  const result = String(text)
    .replaceAll('„', ' «')
    .replaceAll('“', '»')
    .replaceAll(' “', ' «')
    .replaceAll('”', '»')
    .replaceAll(/^"/ig, '«')
    .replaceAll('"', '»')
    .replaceAll('("', '«')
    .replaceAll(['\n»'], ['\n«'])
    .replaceAll([' »'], ' «')
    .replaceAll(['(»'], ['(«']);
  return result;
};

export const calculatedViewMode = mode => {
  if (mode === 1) {
    return {
      with_trashed: true,
      with_archived: true,
    };
  }
  if (mode === 3) {
    return {
      only_archived: true,
    };
  }
  if (mode === 4) {
    return {
      only_trashed: true,
    };
  }
  return {};
};

export const setValueByPath = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  // eslint-disable-next-line no-return-assign
  const target = keys.reduce((acc, key) => acc[key] = { ...acc[key] }, obj);

  if (lastKey) {
    target[lastKey] = value;
    return;
  }
  Object.assign(target, value);
};

export const getExistFields = obj => Object.entries(obj).reduce((acc, [key, value]) => {
  if (value) {
    acc[key] = value;
  }
  return acc;
}, {});

export const esiaToApplicant = user => ({
  legal_entity_type: user?.is_self_employed ? 1 : 2,
  first_name: user?.first_name || '',
  last_name: user?.last_name || '',
  middle_name: user?.middle_name || '',
  gender: user?.gender === 'M' ? GENDER_TYPE.MALE : GENDER_TYPE.FEMALE,
  birth_date: user?.birth_date || '',
  birth_place: user?.birth_place || '',
  citizenship: user?.citizenship ? CITIZENSHIP_TYPE.RU : CITIZENSHIP_TYPE.FOREIGN_CITIZEN,
  snils: String(user?.snils).replace(/\D/g, ''),
  inn: String(user?.inn).replace(/\D/g, ''),
});

export const removeNulls = obj => {
  if (obj) {
    Object.keys(obj).forEach(key => {
      if (obj[key] === null) {
        delete obj[key];
      }
    });
  }
};

export const propsToRow = props => {
  if (props && Array.isArray(props)) {
    return props.join(', ');
  }
  if (props && typeof props === 'object') {
    return Object.values(props).join(', ');
  }
  return props;
};

export const quotesTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = replaceQuotes(String(v))
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const buildAddress = address => {
  const parts = [
    address.index || '',
    address.country || '',
    address.region || '',
    address.area || '',
    address.city || '',
    address.place || '',
    address.location || '',
    address.street || '',
    `${address?.house || address.vladenie || ''}${address.letter || ''}${address.slash || ''}`.trim(),
    address.external || '',
    address.building || '',
    address.room || '',
  ].filter(Boolean);

  return parts.join(', ');
};

export const toLowerCase = {
  output: e => e,
  input: v => String(v).toLowerCase(),
};

export const setRepresentative = (representative, setValue) => {
  if (!representative) {
    return;
  }
  const fields = [
    'last_name',
    'first_name',
    'middle_name',
    'organization_name',
    'inn',
    'address',
    'phone',
    'email',
    'document_of_authority_number',
    'document_of_authority_issue_date',
    'document_of_authority_expiration_date',
  ];

  setFields(representative, 'representative.', fields, setValue);
};

export const setApplicant = (applicant, setValue) => {
  if (!applicant) {
    return;
  }
  const fields = [
    'federal_law_number',
    'legal_entity_type_label',
    'identity_type_label',
    'gender_label',
    'registration_type_label',
    'citizenship_label',
    'legal_entity_type',
    'legal_entity_form',
    'full_name',
    'short_name',
    'ogrn',
    'inn',
    'identity_type',
    'identity_series',
    'identity_number',
    'identity_issue_date',
    'identity_expiration_date',
    'identity_issuer',
    'last_name',
    'first_name',
    'middle_name',
    'phone',
    'email',
    'legal_address',
    'address',
    'office_address',
    'birth_date',
    'place_of_birth',
    'gender',
    'registration_type',
    'snils',
    'citizenship',
    'commercial_name',
    'registration_address',
    'office_phone',
    'office_email',
    'logo_files',
    'isMatch',
    'applicant_id',
    'applicant_status',
    'fact_address',
  ];

  setFields(applicant, 'applicant.', fields, setValue);
};

export const setFields = (data, prefix, fieldsNames, setValue) => {
  if (!fieldsNames?.length || !data) {
    return;
  }
  for (const name of fieldsNames) {
    if (data?.[name]) {
      setValue(`${prefix}${name}`, data?.[name]);
    }
  }
};
